// @ts-nocheck
/**
 * Registers the shared Alpine data provider for every compact ring preview.
 * Each section passes its own JSON-data element ID to keep instances isolated.
 */
(function () {
  /** @param {string} id */
  function factory(id) {
    var config = {};
    var controller;
    var destroyed = false;
    var debugEnabled = new URLSearchParams(location.search).get('ring_preview_debug') === '1';
    try {
      config = JSON.parse(document.getElementById(id)?.textContent || '{}');
    } catch (error) {}

    return {
      loading: true,
      error: false,
      mounting: null,
      variants: [],
      materials: [],
      selectedMaterial: config.material || '',
      engraving: 'Senza incisione',
      engravingText: '',
      ringSize: '12',
      engravingMaxLength: Math.max(1, Math.floor(Number(config.engravingMaxLength) || 25)),
      diamondCurrency: config.diamondCurrency || 'EUR',
      option(variant, name) {
        return variant?.selectedOptions?.find(
          (option) => String(option.name || '').trim().toLowerCase() === name.toLowerCase()
        )?.value || '';
      },
      normalizedMaterial(material) {
        return String(material || '')
          .trim()
          .toLowerCase()
          .replace(/\s*(\d+)\s*kt\b/g, ' $1 kt')
          .replace(/\s+/g, ' ');
      },
      resolvedMaterial(material) {
        var configured = this.materials.find(
          (candidate) => this.normalizedMaterial(candidate) === this.normalizedMaterial(material)
        );
        if (configured) return configured;
        var requested = this.normalizedMaterial(material);
        var color = ['bianco', 'giallo', 'rosa'].find((name) => requested.includes(name));
        if (color) {
          var sameColor = this.materials.find((candidate) => {
            var normalized = this.normalizedMaterial(candidate);
            return normalized.includes(color) && normalized.includes('18 kt');
          });
          if (sameColor) return sameColor;
        }
        return this.materials.find((candidate) => this.normalizedMaterial(candidate) === 'oro giallo 18 kt') || this.materials[0] || '';
      },
      limit(variant) {
        var value = Number(variant?.limit?.value ?? variant?.limit);
        return Number.isFinite(value) && value > 0 ? value : NaN;
      },
      debug(stage, details = {}) {
        if (!debugEnabled) return;
        console.log('[Ring PDP preview]', {
          section: id,
          stage,
          requestedMaterial: config.material || '',
          selectedMaterial: this.selectedMaterial,
          selectValue: this.$refs.materialSelect?.value ?? null,
          availableMaterials: [...this.materials],
          carats: config.carats,
          selectedBand: this.selectedBand?.id || null,
          selectedVariant: this.selected?.id || null,
          ...details,
        });
      },
      syncMaterialSelect() {
        this.$nextTick(() => {
          if (destroyed) return;
          var select = this.$refs.materialSelect;
          if (select) select.value = this.selectedMaterial;
          this.debug('material-synchronized');
        });
      },
      async init() {
        this.$watch('selectedMaterial', () => this.syncMaterialSelect());
        controller = new AbortController();
        this.debug('loading');
        try {
          if (!config.id || !config.token) throw Error('Missing mounting or Storefront token');
          var cursor = null;
          var visited = new Set();
          var variants = [];
          var product;
          do {
            var response = await fetch('/api/2024-01/graphql.json', {
              method: 'POST',
              signal: controller.signal,
              headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': config.token },
              body: JSON.stringify({
                query: 'query($id: ID!, $cursor: String, $country: CountryCode!) @inContext(country: $country) { product(id: $id) { id handle title featuredImage { url } variants(first: 250, after: $cursor) { pageInfo { hasNextPage endCursor } nodes { id title sku availableForSale price { amount currencyCode } limit: metafield(namespace: "ring", key: "diamond_carat_limit") { value } selectedOptions { name value } image { url } } } } }',
                variables: { id: config.id, cursor, country: config.country },
              }),
            });
            var data = await response.json();
            product = data?.data?.product;
            if (!response.ok || data.errors || !Array.isArray(product?.variants?.nodes)) throw Error('Mounting variants unavailable');
            variants.push(...product.variants.nodes);
            var page = product.variants.pageInfo;
            cursor = page?.hasNextPage ? page.endCursor : null;
            if (page?.hasNextPage && (!cursor || visited.has(cursor))) throw Error('Invalid mounting pagination');
            if (cursor) visited.add(cursor);
          } while (cursor);
          if (destroyed) return;
          this.mounting = product;
          this.variants = variants;
          this.materials = [...new Set(this.variants.map((variant) => this.option(variant, 'Materiale')).filter(Boolean))];
          if (!this.materials.length) throw Error('Mounting has no Materiale options');
          this.selectedMaterial = this.resolvedMaterial(config.material);
          this.syncMaterialSelect();
          this.debug('loaded', { variantCount: variants.length, usedFallback: this.normalizedMaterial(config.material) !== this.normalizedMaterial(this.selectedMaterial) });
        } catch (error) {
          if (destroyed) return;
          this.error = true;
          this.debug('error', { message: error instanceof Error ? error.message : String(error) });
        } finally {
          if (!destroyed) this.loading = false;
        }
      },
      destroy() {
        destroyed = true;
        controller?.abort();
      },
      get matching() {
        var carats = Number(config.carats);
        if (!Number.isFinite(carats) || carats <= 0) return [];
        return this.variants.filter((variant) => this.option(variant, 'Materiale') === this.selectedMaterial && this.limit(variant) >= carats);
      },
      get selectedBand() {
        return this.matching.filter((variant) => this.option(variant, 'Incisione') === this.engraving).sort((a, b) => this.limit(a) - this.limit(b))[0] || null;
      },
      get selected() { return this.selectedBand?.availableForSale ? this.selectedBand : null; },
      get image() { return this.selectedBand?.image?.url || this.variants.find((variant) => this.option(variant, 'Materiale') === this.selectedMaterial && variant.image?.url)?.image.url || this.mounting?.featuredImage?.url || ''; },
      metalTone(material) {
        var value = String(material || '').toLowerCase();
        if (value.includes('rosa')) return '#c98270';
        if (value.includes('giallo')) return '#d8ac45';
        return '#e3e0da';
      },
      builderPath() {
        if (!config.builderUrl) return '';
        try {
          var url = new URL(config.builderUrl || '', location.origin);
          return url.origin === location.origin ? url.pathname + url.search : '';
        } catch (error) { return ''; }
      },
      get previewMounting() {
        var variant = this.selected;
        var product = this.mounting;
        if (!variant || !product) return null;
        return { productId: String(product.id).split('/').pop(), variantId: String(variant.id).split('/').pop(), handle: product.handle, title: product.title, variantTitle: variant.title, sku: variant.sku || '', image: this.image, price: Math.round(Number(variant.price.amount) * 100), currency: variant.price.currencyCode || 'EUR', available: true, eligible: false, mounting: true, minimum: 1, increment: 1, material: this.selectedMaterial, engravingOption: this.engraving, engraving: this.engraving === 'Con incisione' ? this.engravingText.trim().slice(0, this.engravingMaxLength) : '', ringSize: this.ringSize, builderUrl: this.builderPath() };
      },
      get actionDisabled() { return this.loading || this.error || !this.selected || this.selected.price.currencyCode !== this.diamondCurrency || !this.builderPath() || (this.engraving === 'Con incisione' && !this.engravingText.trim()); },
      limitEngraving() { if (this.engravingText.length > this.engravingMaxLength) this.engravingText = this.engravingText.slice(0, this.engravingMaxLength); },
      money(variant) { return variant?.price ? new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: variant.price.currencyCode || 'EUR' }).format(Number(variant.price.amount)) : ''; },
      moneyAmount(amount, currency) { return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: currency || 'EUR' }).format(Number(amount || 0) / 100); },
      get totalAmount() { return Math.round(Number(this.selected?.price?.amount || 0) * 100) + Number(config.diamondPrice || 0); },
      get scalapayEligible() {
        var minimum = Number(config.scalapayMinimum);
        var maximum = Number(config.scalapayMaximum);
        return Boolean(this.selected) && this.selected.price.currencyCode === this.diamondCurrency && Number.isFinite(minimum) && Number.isFinite(maximum) && this.totalAmount >= minimum * 100 && this.totalAmount <= maximum * 100;
      },
      get scalapayInstallment() { return this.moneyAmount(Math.round(this.totalAmount / 4), this.selected?.price?.currencyCode || config.diamondCurrency || 'EUR'); },
      get scalapayPrefix() { return config.scalapayPrefix || ''; },
      get scalapayRateSuffix() { return config.scalapayRateSuffix || ''; },
      get scalapaySuffix() { return config.scalapaySuffix || ''; },
    };
  }

  window.customRingPdpPreview = factory;
  document.addEventListener('alpine:init', () => window.Alpine.data('customRingPdpPreview', factory), { once: true });
})();
